import { useMemo, useRef, type ReactNode } from 'react';
import { Box, Dialog } from '@mui/material';
import { styled } from '@mui/material/styles';

import Icon from '../../components/Icon';
import type { CityFile } from '../../shared/cityFile';
import { calendarFeed } from '../../shared/ics/feed';
import { encodeSavedList } from '../../shared/ics/savedList';
import type { Opportunity } from '../../shared/opportunity';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { RsvpStatus } from '../../shared/useRsvp';
import { downloadIcs } from '../schedule/ics';
import { CloseButton, DialogHeading, DialogText, SecondaryAction, SmallPrint, UrlBox, dialogPaperSx } from './dialogParts';
import { feedUrl, sharedCalendarUrl } from './links';
import { useCopy } from './useCopy';

/**
 * Share my calendar (contract §18), the Calendar layout's header button: three
 * ways to take Saved elsewhere, as My Climate Week's Share menu had.
 *
 * 1. Sync with my calendar: a `webcal://` subscription to the Pages Function
 *    feed, whose path is the list itself (there is no storage).
 * 2. Download .ics: the one-off file.
 * 3. Copy a shareable link: `/saved?ids=…&city=…`, a read-only Shared calendar.
 *
 * Back up, Restore and Clear sit behind the "…" at the top, so this is the one
 * place Saved leaves from. All of it works on everything Going or Maybe in
 * this city, whatever the filters show.
 */

const Row = styled('section')({
  display: 'grid',
  gridTemplateColumns: '28px 1fr',
  columnGap: 12,
  padding: '14px 0',
  borderTop: '1px solid rgba(243,238,231,0.12)',
  '&:first-of-type': { borderTop: 'none', paddingTop: 4 },
});

const RowTitle = styled('h3')({
  margin: '3px 0 4px',
  fontFamily: FONTS.display,
  fontSize: 15,
  fontWeight: 700,
});

const Glyph = styled('span')({ gridRow: '1 / span 3', paddingTop: 2, color: BRAND.parchment });

function LinkGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={1.9}
        strokeLinecap="round"
        d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"
      />
    </svg>
  );
}

function DownloadGlyph() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14"
      />
    </svg>
  );
}

const Actions = styled('div')({ display: 'flex', gap: 8, flexWrap: 'wrap', '& > *': { flex: '1 1 150px' } });

interface Props {
  open: boolean;
  onClose: () => void;
  file: CityFile | null;
  /** Every listing, with the user's edits applied. */
  byId: ReadonlyMap<string, Opportunity>;
  /** Going and Maybe, in this city. */
  saved: Readonly<Record<string, RsvpStatus>>;
  notify: (message: string) => void;
  /** Back up, Restore, Clear: Saved's menu as the dialog's overflow. */
  overflow: ReactNode;
}

export default function ShareCalendarDialog({ open, onClose, file, byId, saved, notify, overflow }: Props) {
  const city = file?.city ?? 'nyc';
  const cityShort = file?.cityShort ?? 'NYC';
  const entries = useMemo(() => Object.entries(saved), [saved]);
  const count = entries.length;
  const list = useMemo(() => encodeSavedList(entries), [entries]);
  const webcal = feedUrl(list);
  const shareUrl = sharedCalendarUrl(list, city);

  const feedBox = useRef<HTMLInputElement | null>(null);
  const linkBox = useRef<HTMLInputElement | null>(null);
  const feedCopy = useCopy(() => feedBox.current);
  const linkCopy = useCopy(() => linkBox.current);

  const download = () => {
    if (!file) return;
    const content = calendarFeed({ opportunities: [...byId.values()], cityShort, timezone: file.timezone }, new Map(entries));
    downloadIcs(content, `commonground-${city}.ics`);
    notify(`Downloaded ${count} ${count === 1 ? 'event' : 'events'} as commonground-${city}.ics.`);
  };

  return (
    <Dialog open={open} onClose={onClose} slotProps={{ paper: { sx: { ...dialogPaperSx, maxWidth: 480 } } }}>
      <Box sx={{ position: 'absolute', top: 12, right: 52 }}>{overflow}</Box>
      <CloseButton type="button" aria-label="Close" onClick={onClose}>
        ×
      </CloseButton>
      <DialogHeading>Share my calendar</DialogHeading>
      <DialogText>
        {count
          ? `Your ${count} saved ${count === 1 ? 'event' : 'events'} in ${cityShort}, Going and Maybe.`
          : 'Nothing saved yet. Mark events Going or Maybe and they can go anywhere from here.'}
      </DialogText>

      <Row aria-labelledby="share-sync">
        <Glyph>
          <Icon name="ui:calendar-sync" size={20} />
        </Glyph>
        <RowTitle id="share-sync">Sync with my calendar</RowTitle>
        <div>
          <DialogText sx={{ mb: 1.2 }}>Subscribe in Google, Apple or Outlook calendar. Going shows as busy, Maybe as tentative.</DialogText>
          <UrlBox
            ref={feedBox}
            readOnly
            value={count ? webcal : ''}
            placeholder="Save an event first"
            aria-label="Calendar subscription link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Actions>
            <SecondaryAction type="button" disabled={!count} onClick={() => void feedCopy.copy(webcal)}>
              {feedCopy.copied ? 'Copied' : feedCopy.manual ? 'Selected: copy it' : 'Copy link'}
            </SecondaryAction>
            <SecondaryAction
              type="button"
              disabled={!count}
              onClick={() => {
                window.location.href = webcal;
              }}
            >
              Open in my calendar
            </SecondaryAction>
          </Actions>
          {import.meta.env.DEV && (
            <SmallPrint>Served by the Pages Function, which the Vite dev server doesn't run: see DEPLOYMENT.md.</SmallPrint>
          )}
        </div>
      </Row>

      <Row aria-labelledby="share-ics">
        <Glyph>
          <DownloadGlyph />
        </Glyph>
        <RowTitle id="share-ics">Download .ics</RowTitle>
        <div>
          <DialogText sx={{ mb: 1.2 }}>A one-off file to import anywhere. It won't update.</DialogText>
          {/* The file's name is in the toast; on a phone it wrapped the button onto two lines. */}
          <SecondaryAction type="button" disabled={!count} onClick={download} aria-label={`Download commonground-${city}.ics`}>
            Download the file
          </SecondaryAction>
        </div>
      </Row>

      <Row aria-labelledby="share-link">
        <Glyph>
          <LinkGlyph />
        </Glyph>
        <RowTitle id="share-link">Copy a shareable link</RowTitle>
        <div>
          <DialogText sx={{ mb: 1.2 }}>Anyone with it sees these events on a calendar, and can save them to their own.</DialogText>
          <UrlBox
            ref={linkBox}
            readOnly
            value={count ? shareUrl : ''}
            placeholder="Save an event first"
            aria-label="Shareable link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <SecondaryAction type="button" disabled={!count} onClick={() => void linkCopy.copy(shareUrl)}>
            {linkCopy.copied ? 'Copied' : linkCopy.manual ? 'Selected: copy it' : 'Copy link'}
          </SecondaryAction>
        </div>
      </Row>
    </Dialog>
  );
}
